import { Info } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useProductionPlaceGeoJsonImport } from '@/features/production-declaration/components/statement/production-place/use-production-place-geojson-import';
import type { ProductionPlaceCoordinates } from '@/types/core/production-declaration/production-place';

type ProductionPlaceGeoJsonImportProps = {
  disabled: boolean;
  value: ProductionPlaceCoordinates | null;
  onChange: (geometry: ProductionPlaceCoordinates) => void;
  onPendingChange?: (pending: boolean) => void;
};
const translationPrefix =
  'materialCommodityOrigins.cards.productionPlace.geoJsonImport';

export function ProductionPlaceGeoJsonImport(
  props: ProductionPlaceGeoJsonImportProps,
) {
  const { t } = useTranslation('productionDeclaration');
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const { state, fileName, importFile, confirmImport, cancelImport } =
    useProductionPlaceGeoJsonImport(props);
  const fileNameCharacters = Array.from(fileName ?? '');
  const displayedFileName =
    fileNameCharacters.length > 80
      ? `${fileNameCharacters.slice(0, 79).join('')}…`
      : fileName;
  const { onPendingChange } = props;

  useEffect(() => {
    onPendingChange?.(state.status === 'reading' || state.status === 'confirm');

    return () => onPendingChange?.(false);
  }, [onPendingChange, state.status]);

  return (
    <div className='w-full min-w-0 space-y-2 sm:flex-1'>
      <div className='flex h-5 items-center gap-2.5 sm:justify-end'>
        <span className='font-medium text-sm'>
          {t(`${translationPrefix}.label`)}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={t(`${translationPrefix}.label`)}
              className='rounded-sm text-declaration-muted-foreground transition-colors hover:text-declaration-foreground-muted'
              type='button'
            >
              <Info aria-hidden className='size-5' />
            </button>
          </TooltipTrigger>
          <TooltipContent
            align='start'
            className='w-[min(44rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-declaration-border bg-white p-4 text-left text-declaration-foreground text-sm leading-6 shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
            collisionPadding={24}
            showArrow={false}
            side='bottom'
            sideOffset={12}
          >
            {t(`${translationPrefix}.help`)}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className='flex min-w-0 items-center justify-end gap-3'>
        {fileName ? (
          <span
            className='min-w-0 break-all text-right text-declaration-foreground text-sm'
            title={fileName}
          >
            {displayedFileName}
          </span>
        ) : null}
        <Button
          aria-describedby={`${id}-feedback`}
          className='h-12 shrink-0'
          disabled={props.disabled || state.status === 'reading'}
          onClick={() => inputRef.current?.click()}
          type='button'
          variant='outline'
        >
          {t(`${translationPrefix}.chooseFile`)}
        </Button>
      </div>
      <input
        accept='.geojson,.json,application/geo+json,application/json'
        aria-label={t(`${translationPrefix}.label`)}
        disabled={props.disabled || state.status === 'reading'}
        hidden
        id={id}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];

          event.currentTarget.value = '';

          if (file) {
            importFile(file);
          }
        }}
        ref={inputRef}
        type='file'
      />
      <p
        aria-live='polite'
        className='text-declaration-foreground text-sm empty:hidden sm:text-right'
        id={`${id}-feedback`}
        role='status'
      >
        {state.status === 'error'
          ? t(`${translationPrefix}.${state.error}`)
          : state.status === 'reading'
            ? t(`${translationPrefix}.reading`)
            : null}
      </p>
      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            cancelImport();
          }
        }}
        open={state.status === 'confirm' && !props.disabled}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${translationPrefix}.replaceTitle`)}</DialogTitle>
            <DialogDescription>
              {t(`${translationPrefix}.replaceDescription`)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type='button' variant='outline'>
                {t('cancel', { ns: 'common' })}
              </Button>
            </DialogClose>
            <Button
              disabled={props.disabled}
              onClick={confirmImport}
              type='button'
              variant='declaration-primary'
            >
              {t(`${translationPrefix}.replace`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
